/**
 * EMP-WORKSPACE-001 — step/tab definitions for the Employee Workspace.
 * `optionsKey` selects from the /api/hr/options/* payloads ({value,label}).
 */

export const WORKSPACE_STEPS = [
  {
    id: 'basic',
    title: 'Basic',
    fields: [
      // EMP-MANUAL-ID-001 — HR enters the Employee ID manually (unique,
      // case-insensitive). Immutable after creation (edit mode disables it).
      { key: 'employee_id', label: 'Employee ID', required: true, dupCheck: 'employee_id' },
      { key: 'full_name', label: 'Full Name', required: true },
      { key: 'work_email', label: 'Work Email', required: true, dupCheck: 'work_email' },
      { key: 'team', label: 'Team', type: 'select', optionsKey: 'teams', required: true },
      // HR-RECRUIT-ROLE-001 — descriptive job function (list or typed); never an RBAC grant
      { key: 'role', label: 'Role (job function)', type: 'role', optionsKey: 'roles' },
      { key: 'manager_employee_id', label: 'Manager', type: 'select', optionsKey: 'managers' },
      { key: 'date_of_joining', label: 'Date of Joining', type: 'date', required: true },
      { key: 'work_location', label: 'Work Location', type: 'select', optionsKey: 'locations' },
      { key: 'employee_timezone', label: 'Time Zone', type: 'select', optionsKey: 'timezones' },
      { key: 'notes', label: 'Notes', type: 'textarea', full: true },
    ],
  },
  {
    id: 'employment',
    title: 'Employment',
    fields: [
      { key: 'designation', label: 'Designation', required: true },
      { key: 'employment_type', label: 'Employment Type', type: 'select', options: ['Permanent', 'Probationary', 'Contract', 'Intern'], required: true },
      { key: 'status', label: 'Employment Status', type: 'select', optionsKey: 'statuses', required: true },
      { key: 'last_working_day', label: 'Last Working Day', type: 'date' },
      { key: 'location', label: 'Location', required: true },
      { key: 'grade_level', label: 'Grade / Level' },
    ],
  },
  {
    id: 'personal',
    title: 'Personal',
    fields: [
      { key: 'nationality', label: 'Nationality' },
      { key: 'gender', label: 'Gender', type: 'select', options: ['Male', 'Female', 'Other'] },
      { key: 'date_of_birth', label: 'Date of Birth', type: 'date' },
      { key: 'marital_status', label: 'Marital Status', type: 'select', options: ['Single', 'Married', 'Widowed', 'Divorced'] },
      { key: 'blood_group', label: 'Blood Group' },
      { key: 'personal_email', label: 'Personal Email' },
    ],
  },
  {
    id: 'contact',
    title: 'Contact',
    fields: [
      { key: 'personal_contact_number', label: 'Personal Contact Number' },
      { key: 'official_contact_number', label: 'Official Contact Number' },
      { key: 'emergency_contact_name', label: 'Emergency Contact Name' },
      { key: 'emergency_contact_number', label: 'Emergency Contact Number' },
      { key: 'emergency_contact_relationship', label: 'Relationship' },
      { key: 'permanent_address', label: 'Permanent Address', type: 'textarea', full: true },
      { key: 'present_address', label: 'Present Address', type: 'textarea', full: true },
    ],
  },
  {
    id: 'statutory',
    title: 'Statutory',
    sensitive: true,
    fields: [
      { key: 'pan_number', label: 'PAN Number' },
      { key: 'aadhaar_number', label: 'Aadhaar Number' },
      { key: 'uan', label: 'UAN' },
      { key: 'member_id', label: 'Member ID' },
      { key: 'esi_number', label: 'ESI' },
      { key: 'ghi_member_id', label: 'Group Health Insurance Member ID' },
    ],
  },
  {
    id: 'banking',
    title: 'Banking',
    fields: [
      { key: 'bank_name', label: 'Bank Name' },
      { key: 'bank_account_number', label: 'Bank Account Number' },
      { key: 'ifsc_code', label: 'IFSC Code' },
    ],
  },
];

export const ALL_WORKSPACE_KEYS = WORKSPACE_STEPS.flatMap(s => s.fields.map(f => f.key));

export const buildEmptyWorkspaceForm = () => ({
  ...Object.fromEntries(ALL_WORKSPACE_KEYS.map(k => [k, ''])),
  work_location: 'office',
  employee_timezone: 'UTC',
  status: 'active',
  employment_type: 'Permanent',
  date_of_joining: new Date().toISOString().split('T')[0],
});

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const validateStep = (step, formData) => {
  const errors = {};
  step.fields.forEach(f => {
    const v = (formData[f.key] || '').toString().trim();
    if (f.required && !v) errors[f.key] = `${f.label} is required`;
  });
  if (step.id === 'basic') {
    const email = (formData.work_email || '').trim();
    if (email && !EMAIL_RE.test(email)) errors.work_email = 'Invalid email format';
    const role = (formData.role || '').trim();
    if (role && role.length < 2) errors.role = 'Role must be at least 2 characters'; // HR-RECRUIT-ROLE-001 (optional field)
  }
  if (step.id === 'personal') {
    const email = (formData.personal_email || '').trim();
    if (email && !EMAIL_RE.test(email)) errors.personal_email = 'Invalid email format';
  }
  return errors;
};
